import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { visitsAPI, stationsAPI, officeLocationsAPI, usersAPI } from "../../../shared/lib/api";
import { useAuth } from "../../../shared/context/AuthContext";
import { Button } from "../../../shared/components/ui/button";
import { Card, CardContent } from "../../../shared/components/ui/card";
import { Badge } from "../../../shared/components/ui/badge";
import { Label } from "../../../shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../shared/components/ui/select";
import { Calendar } from "../../../shared/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../shared/components/ui/popover";
import { toast } from "sonner";
import {
  History,
  ArrowLeft,
  Search,
  Eye,
  Building2,
  Calendar as CalendarIcon,
  Download,
  User,
  X,
  FileText,
} from "lucide-react";
import {
  formatDateTime,
  getDuration,
  PURPOSE_LABELS,
  STATUS_COLORS,
  cn,
} from "../../../shared/lib/utils";
import { format, subDays, startOfMonth, endOfMonth } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Quick date preset ranges
const toYMD = (d) => format(d, "yyyy-MM-dd");
const presetRanges = {
  today: () => {
    const now = new Date();
    return { from: toYMD(now), to: toYMD(now) };
  },
  last7: () => {
    const now = new Date();
    return { from: toYMD(subDays(now, 6)), to: toYMD(now) };
  },
  last30: () => {
    const now = new Date();
    return { from: toYMD(subDays(now, 29)), to: toYMD(now) };
  },
  thisMonth: () => {
    const now = new Date();
    return { from: toYMD(startOfMonth(now)), to: toYMD(endOfMonth(now)) };
  },
};

const PRESET_LABELS = {
  today: "Today",
  last7: "Last 7 Days",
  last30: "Last 30 Days",
  thisMonth: "This Month",
};

export default function HistoryPage() {
  const navigate = useNavigate();
  const { user, isSupervisor } = useAuth();

  const [visits, setVisits] = useState([]);
  const [stations, setStations] = useState([]);
  const [officeLocations, setOfficeLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [selectedStation, setSelectedStation] = useState("");
  const [selectedOffice, setSelectedOffice] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [quickPreset, setQuickPreset] = useState(null); // "today" | "last7" | "last30" | "thisMonth" | null

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const promises = [
        visitsAPI.getHistory({ limit: 100 }),
        stationsAPI.getAll(),
        officeLocationsAPI.getAll(),
      ];
      
      // Only load users list if supervisor or admin
      if (isSupervisor()) {
        promises.push(usersAPI.getAll());
      }
      
      const results = await Promise.all(promises);
      setVisits(results[0].data);
      setStations(results[1].data);
      setOfficeLocations(results[2].data);
      
      if (isSupervisor() && results[3]) {
        setUsers(results[3].data);
      }
    } catch (err) {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  // Format date as YYYY-MM-DD for API
  const formatDateForAPI = (date) => {
    if (!date) return null;
    return format(date, "yyyy-MM-dd");
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (selectedStation) params.station_id = selectedStation;
      if (selectedOffice) params.office_location_id = selectedOffice;
      if (selectedUser) params.user_id = selectedUser;
      if (startDate) params.start_date = formatDateForAPI(startDate);
      if (endDate) params.end_date = formatDateForAPI(endDate);

      const res = await visitsAPI.getHistory(params);
      setVisits(res.data);
    } catch (err) {
      toast.error("Failed to search history");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSelectedStation("");
    setSelectedOffice("");
    setSelectedUser("");
    setStartDate(null);
    setEndDate(null);
    setQuickPreset(null);
  };

  // Apply quick date preset
  const applyPreset = async (key) => {
    const { from, to } = presetRanges[key]();
    setQuickPreset(key);
    // Convert strings back to Date objects for the calendar UI
    setStartDate(new Date(from));
    setEndDate(new Date(to));
    
    // Immediately search with the preset dates
    setLoading(true);
    try {
      const params = { limit: 100, start_date: from, end_date: to };
      if (selectedStation) params.station_id = selectedStation;
      if (selectedOffice) params.office_location_id = selectedOffice;
      if (selectedUser) params.user_id = selectedUser;

      const res = await visitsAPI.getHistory(params);
      setVisits(res.data);
    } catch (err) {
      toast.error("Failed to search history");
    } finally {
      setLoading(false);
    }
  };

  // Clear preset when manually selecting dates
  const handleStartDateChange = (date) => {
    setStartDate(date);
    setQuickPreset(null);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    setQuickPreset(null);
  };

  const handleExportCSV = () => {
    if (visits.length === 0) {
      toast.error("No data to export");
      return;
    }

    const headers = [
      "Visit ID",
      "Station",
      "User",
      "Role",
      "Entry Time",
      "Exit Time",
      "Duration",
      "Purpose",
      "Status",
      "Work Order",
    ];

    const rows = visits.map((v) => [
      v.id,
      v.station_name,
      v.user_name,
      v.user_role,
      v.entry_time,
      v.exit_time || "",
      v.exit_time ? getDuration(v.entry_time, v.exit_time) : "",
      PURPOSE_LABELS[v.purpose] || v.purpose,
      v.status,
      v.work_order_number || "",
    ]);

    const csvContent = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `station-log-history-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  const handleExportPDF = () => {
    if (visits.length === 0) {
      toast.error("No data to export");
      return;
    }

    const doc = new jsPDF({ orientation: "landscape" });
    
    // Title
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("Station Log Book - Visit History", 14, 15);
    
    // Subtitle with date range
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const dateRange = startDate && endDate 
      ? `${format(startDate, "yyyy-MM-dd")} to ${format(endDate, "yyyy-MM-dd")}`
      : "All dates";
    doc.text(`Generated: ${format(new Date(), "yyyy-MM-dd HH:mm")} | Date Range: ${dateRange}`, 14, 22);
    doc.text(`Total Records: ${visits.length}`, 14, 27);

    // Table data
    const tableHeaders = [
      "Station",
      "User",
      "Role",
      "Entry Time",
      "Exit Time",
      "Duration",
      "Purpose",
      "Status",
      "Work Order",
    ];

    const tableData = visits.map((v) => [
      v.station_name || "-",
      v.user_name || "-",
      v.user_role || "-",
      v.entry_time ? formatDateTime(v.entry_time) : "-",
      v.exit_time ? formatDateTime(v.exit_time) : "-",
      v.exit_time ? getDuration(v.entry_time, v.exit_time) : "-",
      PURPOSE_LABELS[v.purpose] || v.purpose || "-",
      (v.status || "-").toUpperCase(),
      v.work_order_number || "-",
    ]);

    // Generate table
    autoTable(doc, {
      head: [tableHeaders],
      body: tableData,
      startY: 32,
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [255, 107, 0], // #FF6B00
        textColor: 255,
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      columnStyles: {
        0: { cellWidth: 35 }, // Station
        1: { cellWidth: 30 }, // User
        2: { cellWidth: 20 }, // Role
        3: { cellWidth: 35 }, // Entry Time
        4: { cellWidth: 35 }, // Exit Time
        5: { cellWidth: 20 }, // Duration
        6: { cellWidth: 30 }, // Purpose
        7: { cellWidth: 20 }, // Status
        8: { cellWidth: 25 }, // Work Order
      },
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Page ${i} of ${pageCount} | Station Log Book`,
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 10,
        { align: "center" }
      );
    }

    // Save the PDF
    doc.save(`station-log-history-${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("PDF exported");
  };

  if (loading && visits.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner h-8 w-8 border-[#FF6B00]" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6" data-testid="history-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="font-mono text-sm"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="font-heading font-black text-2xl flex items-center gap-2">
            <History className="h-6 w-6 text-[#FF6B00]" />
            Log History
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleExportPDF}
            className="rounded-none border-2 font-mono"
            data-testid="export-pdf-btn"
          >
            <FileText className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
          <Button
            variant="outline"
            onClick={handleExportCSV}
            className="rounded-none border-2 font-mono"
            data-testid="export-csv-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-2 border-border rounded-none mb-6">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {/* Station Filter */}
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-widest">
                Station
              </Label>
              <Select value={selectedStation || "all"} onValueChange={(val) => setSelectedStation(val === "all" ? "" : val)}>
                <SelectTrigger
                  className="h-10 rounded-none border-2 font-mono"
                  data-testid="filter-station-select"
                >
                  <SelectValue placeholder="All stations" />
                </SelectTrigger>
                <SelectContent className="rounded-none max-h-60">
                  <SelectItem value="all">All Stations</SelectItem>
                  {stations.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.station_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Office Location Filter */}
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-widest flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                Office
              </Label>
              <Select value={selectedOffice || "all"} onValueChange={(val) => setSelectedOffice(val === "all" ? "" : val)}>
                <SelectTrigger
                  className="h-10 rounded-none border-2 font-mono"
                  data-testid="filter-office-select"
                >
                  <SelectValue placeholder="All offices" />
                </SelectTrigger>
                <SelectContent className="rounded-none max-h-60">
                  <SelectItem value="all">All Offices</SelectItem>
                  {officeLocations.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.office_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* User Filter (Supervisors/Admins only) */}
            {isSupervisor() && (
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-widest flex items-center gap-1">
                  <User className="h-3 w-3" />
                  User
                </Label>
                <Select value={selectedUser || "all"} onValueChange={(val) => setSelectedUser(val === "all" ? "" : val)}>
                  <SelectTrigger
                    className="h-10 rounded-none border-2 font-mono"
                    data-testid="filter-user-select"
                  >
                    <SelectValue placeholder="All users" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none max-h-60">
                    <SelectItem value="all">All Users</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} ({u.employee_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Start Date */}
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-widest">
                From Date
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full h-10 justify-start text-left font-mono rounded-none border-2",
                      !startDate && "text-muted-foreground"
                    )}
                    data-testid="filter-start-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "yyyy-MM-dd") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-none" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={handleStartDateChange}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* End Date */}
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-widest">
                To Date
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full h-10 justify-start text-left font-mono rounded-none border-2",
                      !endDate && "text-muted-foreground"
                    )}
                    data-testid="filter-end-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, "yyyy-MM-dd") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-none" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={handleEndDateChange}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Action Buttons */}
            <div className="flex items-end gap-2">
              <Button
                onClick={handleSearch}
                disabled={loading}
                className="flex-1 h-10 rounded-sm bg-[#FF6B00] hover:bg-[#E65100] font-mono uppercase tracking-wider"
                data-testid="search-btn"
              >
                <Search className="h-4 w-4 mr-2" />
                Search
              </Button>
              <Button
                variant="outline"
                onClick={clearFilters}
                className="h-10 rounded-none border-2 font-mono"
                title="Clear filters"
                data-testid="clear-filters-btn"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Quick Date Presets */}
          <div className="mt-4 pt-4 border-t border-border">
            <Label className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
              Quick Date Filters
            </Label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(PRESET_LABELS).map(([key, label]) => (
                <Button
                  key={key}
                  variant={quickPreset === key ? "default" : "outline"}
                  size="sm"
                  onClick={() => applyPreset(key)}
                  disabled={loading}
                  className={cn(
                    "rounded-none font-mono text-xs",
                    quickPreset === key 
                      ? "bg-[#FF6B00] hover:bg-[#E65100] border-[#FF6B00]" 
                      : "border-2"
                  )}
                  data-testid={`preset-${key}`}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {visits.length === 0 ? (
        <Card className="border-2 border-dashed border-border rounded-none">
          <CardContent className="p-8 text-center">
            <History className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
            <p className="font-body text-muted-foreground">No visits found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {visits.map((visit) => (
            <Card
              key={visit.id}
              className="border-2 border-border rounded-none industrial-card cursor-pointer"
              onClick={() => navigate(`/visit/${visit.id}`)}
              data-testid={`history-visit-${visit.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-heading font-bold">
                        {visit.station_name}
                      </span>
                      <Badge
                        className={`rounded-none font-mono text-xs border ${
                          STATUS_COLORS[visit.status]
                        }`}
                      >
                        {visit.status.toUpperCase()}
                      </Badge>
                      <Badge
                        variant="outline"
                        className="rounded-none font-mono text-xs"
                      >
                        {PURPOSE_LABELS[visit.purpose] || visit.purpose}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm flex-wrap">
                      <span className="font-mono text-muted-foreground">
                        {visit.user_name}
                      </span>
                      {visit.work_order_number && (
                        <span className="font-mono text-muted-foreground">
                          WO: {visit.work_order_number}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs font-mono text-muted-foreground">
                      <span>Entry: {formatDateTime(visit.entry_time)}</span>
                      {visit.exit_time && (
                        <>
                          <span>Exit: {formatDateTime(visit.exit_time)}</span>
                          <span>
                            Duration: {getDuration(visit.entry_time, visit.exit_time)}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-none font-mono text-xs"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
